import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import LoadingIconFull from '~/Common-Objects/LoadingIcons/loadingIconFull'
import axios from '~/data/http/axios'
import i18n from '~/i18n'
import Mailchimp2Icons from './icons'
import { MAILCHIMP_FLOW_PATH } from '~/pages/cp/import/paths'
import { leaveMailchimpFlow } from '~/pages/cp/import/mailchimpFlowOrigin'
import {
    captureMailchimpImportBackgrounded,
    captureMailchimpImportCompleted,
    captureMailchimpImportFailed
} from '~/utils/mailchimpImportAnalytics'

const EMPTY_POLL_LIMIT = 3

export default function Mailchimp2ImportPage({ onBackground }) {
    const history = useHistory()
    const [job, setJob] = useState(null)
    const [leaving, setLeaving] = useState(false)
    const timer = useRef(null)
    const mounted = useRef(true)
    const emptyPolls = useRef(0)
    const prevStatus = useRef(null)

    const poll = async () => {
        try {
            const res = await axios.get('/application/mailchimp/migrate')
            if (!mounted.current) return
            if (res.data.job) {
                emptyPolls.current = 0
                setJob(res.data.job)
                const status = res.data.job.status
                // Only on a live running → terminal transition, so revisiting a finished job is not re-counted
                const wasRunning = prevStatus.current === 'running'
                prevStatus.current = status
                if (status === 'complete') {
                    if (wasRunning) captureMailchimpImportCompleted(res.data.job)
                    if (timer.current) clearInterval(timer.current)
                } else if (status === 'error') {
                    if (wasRunning) captureMailchimpImportFailed(res.data.job)
                    if (timer.current) clearInterval(timer.current)
                }
                return
            }
            emptyPolls.current += 1
            if (emptyPolls.current >= EMPTY_POLL_LIMIT) {
                history.replace(MAILCHIMP_FLOW_PATH)
            }
        } catch (err) {
            if (!mounted.current) return
            history.replace(MAILCHIMP_FLOW_PATH)
        }
    }

    useEffect(() => {
        mounted.current = true
        poll()
        timer.current = setInterval(poll, 2000)
        return () => {
            mounted.current = false
            if (timer.current) clearInterval(timer.current)
        }
    }, [])

    if (leaving && !job) {
        return <LoadingIconFull />
    }

    const percent = job ? job.percentComplete || 0 : 0
    const complete = !!(job && job.status === 'complete')
    const progressLabel = complete
        ? i18n.t('mailchimp2:done.title')
        : job
          ? `${i18n.t('mailchimp2:import.step', { step: job.stage || 1 })} — ${job.stageLabel || ''}`
          : i18n.t('mailchimp2:import.starting')

    return (
        <div className='mailchimp2-page'>
            <Mailchimp2Icons animateConnector={!complete} />
            <h1 className='mailchimp2-title'>
                {complete ? i18n.t('mailchimp2:done.title') : i18n.t('mailchimp2:import.title')}
            </h1>
            <div className='mailchimp2-progress'>
                <div className='mailchimp2-progress__meta'>
                    <div className='mailchimp2-progress__label'>{progressLabel}</div>
                    <div className='mailchimp2-progress__percent'>{percent}%</div>
                </div>
                <div className='mailchimp2-progress__track'>
                    <div className='mailchimp2-progress__fill' style={{ width: percent + '%' }} />
                </div>
                {!!(job && job.currentObject) && <div className='mailchimp2-progress__object'>{job.currentObject}</div>}
            </div>
            {!complete && (
                <div className='mailchimp2-actions'>
                    <Button
                        text={i18n.t('mailchimp2:import.background')}
                        className='button-sml grey'
                        onClick={() => {
                            captureMailchimpImportBackgrounded(job)
                            if (onBackground) onBackground()
                            else leaveMailchimpFlow(history)
                        }}
                    />
                </div>
            )}
            {complete && (
                <div className='mailchimp2-actions'>
                    <Button
                        text={i18n.t('mailchimp2:bar.see')}
                        className='button-primary'
                        onClick={async () => {
                            setLeaving(true)
                            try {
                                await axios.post('/application/mailchimp/migrate/dismiss')
                            } catch (err) {}
                            history.replace(MAILCHIMP_FLOW_PATH)
                        }}
                    />
                </div>
            )}
        </div>
    )
}
